/**
 * Link Check page — Settings → Link Check
 *
 * Pick a collection (or All), start a background scan, watch progress.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Play, RefreshCw, AlertCircle } from 'lucide-react';
import {
    triggerScan,
    getLatestScan,
    getUrlCollections,
    type ScanJob,
} from '@/api/link-check';

const POLL_INTERVAL_MS = 3000;
const ALL_COLLECTIONS = '__all__';

export function LinkCheckPage() {
    const { data: collectionOptions = [], isLoading: loadingCollections } = useQuery({
        queryKey: ['link-check-url-collections'],
        queryFn: getUrlCollections,
    });
    const [selected, setSelected] = useState<string>(ALL_COLLECTIONS);
    const [job, setJob] = useState<ScanJob | null>(null);
    const [loading, setLoading] = useState(false);
    const [starting, setStarting] = useState(false);
    const pollRef = useRef<number | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getLatestScan()
            .then((j) => {
                if (!cancelled) setJob(j);
            })
            .catch((err) => {
                if (!cancelled) toast.error(err?.message ?? 'Failed to load scan status');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (job?.status === 'running') {
            startPolling();
        } else {
            stopPolling();
        }
        return stopPolling;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job?.status]);

    const startPolling = () => {
        stopPolling();
        pollRef.current = window.setInterval(async () => {
            try {
                const latest = await getLatestScan();
                setJob(latest);
                if (latest?.status !== 'running') stopPolling();
            } catch {
                // ignore transient errors during polling
            }
        }, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
        if (pollRef.current !== null) {
            window.clearInterval(pollRef.current);
            pollRef.current = null;
        }
    };

    const handleStart = async () => {
        setStarting(true);
        try {
            const { job: newJob, alreadyRunning } = await triggerScan(
                selected === ALL_COLLECTIONS ? undefined : selected,
            );
            setJob(newJob);
            if (alreadyRunning) {
                toast.info('A scan is already running');
            } else {
                toast.success('Link check started');
            }
        } catch (err: any) {
            toast.error(err?.message ?? 'Failed to start scan');
        } finally {
            setStarting(false);
        }
    };

    const progressPct = useMemo(() => {
        if (!job || job.total === 0) return 0;
        return Math.min(100, Math.round((job.checked / job.total) * 100));
    }, [job]);

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader>
                    <CardTitle>Link Check</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium">Collection</label>
                            <Select
                                value={selected}
                                onValueChange={setSelected}
                                disabled={loadingCollections || collectionOptions.length === 0}
                            >
                                <SelectTrigger className="w-64">
                                    <SelectValue placeholder="Choose a collection" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ALL_COLLECTIONS}>All Collections</SelectItem>
                                    {collectionOptions.map((c) => (
                                        <SelectItem key={c} value={c}>
                                            {c}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button
                            onClick={handleStart}
                            disabled={
                                starting ||
                                job?.status === 'running' ||
                                collectionOptions.length === 0
                            }
                            className="gap-2"
                        >
                            {job?.status === 'running' ? (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                                <Play className="h-4 w-4" />
                            )}
                            {job?.status === 'running' ? 'Scan running' : 'Start Check'}
                        </Button>
                    </div>

                    {!loadingCollections && collectionOptions.length === 0 && (
                        <div className="rounded-md border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground">
                            No collections have a URL field yet. Add an <span className="font-mono">input-url</span>{' '}
                            field to a collection from Data Model to start tracking link health.
                        </div>
                    )}

                    {loading && <p className="text-sm text-muted-foreground">Loading status…</p>}

                    {job?.status === 'running' && <CurrentScanPanel job={job} percent={progressPct} />}

                    {job && job.status !== 'running' && <LastScanPanel job={job} />}

                    <div className="text-sm">
                        <Link className="text-primary hover:underline" to="/admin/settings/dead-links">
                            View Dead Links Report →
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

function CurrentScanPanel({ job, percent }: { job: ScanJob; percent: number }) {
    return (
        <div className="space-y-3 rounded-lg border bg-muted/40 p-4">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">Current scan</h3>
                <Badge>Running</Badge>
            </div>
            <div className="text-sm text-muted-foreground">
                Started {new Date(job.started_at).toLocaleString()}
                {job.collection && <> · Collection: <span className="font-medium">{job.collection}</span></>}
            </div>
            <div className="space-y-1">
                <Progress value={percent} />
                <div className="text-xs text-muted-foreground">
                    {job.checked} / {job.total || '?'} checked · {job.dead} dead so far
                </div>
            </div>
        </div>
    );
}

function LastScanPanel({ job }: { job: ScanJob }) {
    const ts = job.completed_at ? new Date(job.completed_at) : new Date(job.started_at);
    const variant: 'default' | 'destructive' = job.status === 'completed' ? 'default' : 'destructive';
    const label = job.status === 'completed' ? 'Completed' : 'Failed';
    return (
        <div className="space-y-2 rounded-lg border p-4">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">Last scan</h3>
                <Badge variant={variant}>{label}</Badge>
            </div>
            <div className="text-sm text-muted-foreground">
                {ts.toLocaleString()}
                {job.collection && <> · Collection: <span className="font-medium">{job.collection}</span></>}
            </div>
            <div className="text-sm">
                {job.checked} checked · <span className="font-semibold text-destructive">{job.dead} dead</span>
            </div>
            {job.status === 'failed' && job.error_message && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{job.error_message}</span>
                </div>
            )}
        </div>
    );
}
